/* Dr.Primal — the PDP gallery.

   Every frame is rendered by Liquid and sits in the DOM; this file only toggles which
   one is visible and which thumbnail reads as pressed. No image is fetched, swapped or
   constructed here, so the responsive attributes Liquid wrote are the ones that ship.

   The thumbnails are real <button>s, so keyboard and screen-reader behaviour is the
   platform's rather than something reimplemented here. */
(function () {
  'use strict';

  function init() {
    var gallery = document.querySelector('[data-dp-gallery]');
    if (!gallery) return;

    var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-dp-gallery-thumb]'));
    var frames = Array.prototype.slice.call(gallery.querySelectorAll('[data-dp-gallery-frame]'));
    if (!thumbs.length || !frames.length) return;

    var stage = frames[0].parentElement;
    var current = 0;

    /* TWO MODES, ONE SET OF FRAMES.

       Wide, the stage shows one frame and the rest carry `hidden` — the behaviour this
       file has always had. Narrow, the stylesheet turns the stage into a scroll-snap
       carousel so the finger drags the picture directly, and `hidden` has to come OFF
       every frame: a hidden element has no box, so it cannot be scrolled to and the
       snap points collapse to one.

       Which mode is live is read from the same 900px breakpoint the stylesheet uses.
       That duplication is deliberate and unavoidable — a media query is not visible to
       script — so it is stated here rather than inferred from a width comparison that
       would drift the first time the breakpoint moves. */
    /* Guarded the same way assets/dp-media-video.js, dp-parallax.js and dp-recognition.js
       already guard `matchMedia` in this theme (caught on review, 2026-09-03): calling it
       unconditionally made a previously optional enhancement a hard dependency — before
       this file grew a carousel mode it had zero reliance on `matchMedia`, and a
       TypeError here would abort `init()` before the thumbnail click handler or the wide-
       layout swipe handler ever got attached, breaking the WHOLE gallery rather than just
       the carousel. Fails closed on both: no carousel (the original swipe-to-step
       behaviour) and no reduced-motion opt-out (jump the scroll rather than assume motion
       is wanted when the preference can't be read). */
    var carouselMq = window.matchMedia ? window.matchMedia('(max-width: 900px)') : null;
    /* THE THIRD MODE (2026-09-22). Above 1320px the stylesheet lays every frame out in a
       two-column grid — no stage, no rail — so, like the carousel, no frame may carry
       `hidden`. Same duplication of the stylesheet's breakpoint as above, for the same
       reason. The scroll listener and the wide-layout swipe both stay off in this mode:
       there is nothing to scroll or step through when everything is already showing. */
    var gridMq = window.matchMedia ? window.matchMedia('(min-width: 1320px)') : null;
    var reduceMq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    var carousel = false;
    var grid = false;

    /* The frames are NARROWER than the stage below the breakpoint — a peeking carousel
       with a gap and an inset (2026-09-22) — so "index times stage width" no longer
       lands anywhere useful. Both directions read the real geometry instead: the target
       is the scroll offset that centres the frame in the stage, which the browser then
       clamps to the ends exactly as `scroll-snap-align: center` does for the first and
       last frames. */
    function frameCenterOffset(index) {
      var frame = frames[index];
      if (!frame) return 0;
      var frameRect = frame.getBoundingClientRect();
      var stageRect = stage.getBoundingClientRect();
      return stage.scrollLeft + (frameRect.left + frameRect.width / 2) - (stageRect.left + stageRect.width / 2);
    }

    function nearestFrameIndex() {
      var stageRect = stage.getBoundingClientRect();
      var center = stageRect.left + stageRect.width / 2;
      var best = 0;
      var bestDistance = Infinity;
      frames.forEach(function (frame, i) {
        var rect = frame.getBoundingClientRect();
        var distance = Math.abs(rect.left + rect.width / 2 - center);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      });
      return best;
    }

    function scrollToIndex(index, behavior) {
      var left = frameCenterOffset(index);
      if (!stage.scrollTo) {
        stage.scrollLeft = left;
        return;
      }
      stage.scrollTo({
        left: left,
        // Reduced motion gets the same destination without the travel. `dr-primal.css`
        // cannot reach this one: scroll behaviour is an argument here, not a duration.
        behavior: !reduceMq || reduceMq.matches ? 'auto' : behavior || 'smooth',
      });
    }

    /* ONE frame-visibility rule, not two hand-synced copies of it (deduped on review,
       2026-09-03) — `show()` and `setMode()` used to each carry their own `forEach`
       computing the same thing, `setMode`'s a strict superset of `show`'s. Edit one,
       forget the other was the exact drift risk; this file's own carousel-mode comment
       above already warns about state disagreeing across the two modes. */
    function syncFrames() {
      frames.forEach(function (frame) {
        frame.hidden = carousel || grid ? false : frame.getAttribute('data-dp-gallery-frame') !== String(current);
      });
    }

    /* `fromScroll` marks the call the scroll listener makes. Without it, syncing the
       thumbs after a swipe would scroll the stage back to where it already is, which
       fights the momentum the finger just handed it. */
    function show(index, fromScroll) {
      current = parseInt(index, 10) || 0;
      thumbs.forEach(function (thumb) {
        var pressed = thumb.getAttribute('data-dp-gallery-thumb') === String(current);
        thumb.setAttribute('aria-pressed', pressed ? 'true' : 'false');
        // The thumb row scrolls horizontally; a selection made by swiping the stage
        // has to bring its thumb into the window or the pressed state is invisible.
        if (pressed && thumb.scrollIntoView) {
          thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
      });

      if (carousel) {
        if (!fromScroll) scrollToIndex(current);
        return;
      }

      syncFrames();
    }

    function setMode() {
      carousel = !!carouselMq && carouselMq.matches;
      grid = !!gridMq && gridMq.matches;
      syncFrames();
      // Jump, never glide, when the mode itself changed: the reader did not ask to move
      // through the set, they resized or rotated.
      if (carousel) scrollToIndex(current, 'auto');
    }

    setMode();

    [carouselMq, gridMq].forEach(function (mq) {
      if (mq && mq.addEventListener) mq.addEventListener('change', setMode);
    });

    /* Which frame is showing is derived from where the stage has been scrolled to, so a
       flick that crosses two frames, an interrupted scroll and a snap that lands somewhere
       unexpected all report the truth rather than an assumed step. rAF keeps it to one
       read a frame. */
    var scrollQueued = false;

    stage.addEventListener(
      'scroll',
      function () {
        if (!carousel || scrollQueued) return;
        scrollQueued = true;
        window.requestAnimationFrame(function () {
          scrollQueued = false;
          if (!stage.clientWidth) return;
          var index = nearestFrameIndex();
          if (index !== current) show(String(index), true);
        });
      },
      { passive: true }
    );

    /* THE LIGHTBOX. Same machinery as assets/dp-ugc-modal.js: one native <dialog>, so
       focus trapping, Escape and the backdrop are the platform's. The dialog's <img> gets
       its src/srcset written on open — copied from the frame the reader is already looking
       at, so the browser can serve the cached candidate or fetch a sharper one for the
       larger sizes hint — and removed again on close.

       Fails closed: no dialog element, no showModal support, or a frame with no <img>
       (the designed placeholder state) and nothing opens; the zoom discs are hidden up
       front when the dialog can't work so they never read as broken buttons. */
    var modal = document.querySelector('[data-dp-gallery-modal]');
    var modalImg = modal ? modal.querySelector('[data-dp-gallery-modal-img]') : null;
    var modalStage = modal ? modal.querySelector('[data-dp-gallery-modal-stage]') : null;
    var modalReady = !!(modal && modalImg && modalStage && typeof modal.showModal === 'function');

    if (!modalReady) {
      Array.prototype.slice.call(gallery.querySelectorAll('[data-dp-gallery-zoom]')).forEach(function (zoom) {
        zoom.hidden = true;
      });
    }

    /* PINCH, DRAG, DOUBLE-TAP AND WHEEL ZOOM, ON THE PHOTOGRAPH ALONE (2026-09-22).

       The stage carries `touch-action: none`, so the browser gives up its own pinch (page
       zoom) and pan inside the dialog and reports raw pointers instead. Everything below
       is one scale and one offset, written to the image as a transform; the page's own
       zoom level is never touched, and the close disc — fixed, outside the image — never
       moves. Scale is clamped to 1..4 and the offset is clamped so the picture can be
       dragged only as far as it overhangs the viewport: at 1x it cannot be dragged at
       all, which is what makes the letterbox tap-to-close below unambiguous.

       Two fingers scale about their midpoint; the offset is adjusted so the point under
       the fingers stays under the fingers. One finger drags when zoomed. A second tap
       within 300ms toggles between fit and 2.5x at the tap point; a double-click does the
       same for a mouse, and the wheel zooms about the cursor. `+`, `-` and `0` do it from
       the keyboard, since a dialog with no keyboard route to the zoom would leave
       keyboard readers with a smaller picture than everyone else. */
    var ZOOM_MIN = 1;
    var ZOOM_MAX = 4;
    var ZOOM_TAP = 2.5;
    /* A pinch that maps 1:1 to the fingers' spread felt twitchy (client, 2026-09-22), so
       the spread ratio is raised to this power: doubling the gap between the fingers now
       zooms by about 1.6x rather than 2x, and small tremors barely register. Below 1 is
       calmer, 1 is the raw gesture. The wheel step is softened to match. */
    var PINCH_DAMPING = 0.65;
    var WHEEL_STEP = 1.08;
    var zoom = { scale: 1, x: 0, y: 0 };
    var pointers = {};
    var pointerCount = 0;
    var pinchStart = null;
    var dragStart = null;
    var moved = false;
    var lastTap = 0;
    var lastTapX = 0;
    var lastTapY = 0;

    function applyZoom() {
      var stageRect = modalStage.getBoundingClientRect();
      var imgW = modalImg.offsetWidth * zoom.scale;
      var imgH = modalImg.offsetHeight * zoom.scale;
      var maxX = Math.max(0, (imgW - stageRect.width) / 2);
      var maxY = Math.max(0, (imgH - stageRect.height) / 2);
      zoom.x = Math.min(maxX, Math.max(-maxX, zoom.x));
      zoom.y = Math.min(maxY, Math.max(-maxY, zoom.y));
      modalImg.style.transform =
        'translate(' + zoom.x + 'px, ' + zoom.y + 'px) scale(' + zoom.scale + ')';
      if (zoom.scale > 1) {
        modalStage.setAttribute('data-dp-zoomed', '');
      } else {
        modalStage.removeAttribute('data-dp-zoomed');
      }
    }

    function resetZoom() {
      zoom = { scale: 1, x: 0, y: 0 };
      pointers = {};
      pointerCount = 0;
      pinchStart = null;
      dragStart = null;
      modalStage.removeAttribute('data-dp-dragging');
      applyZoom();
    }

    /* Scale about a viewport point: the offset moves so that point stays fixed. */
    function zoomAbout(nextScale, clientX, clientY) {
      nextScale = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, nextScale));
      var stageRect = modalStage.getBoundingClientRect();
      var cx = clientX - (stageRect.left + stageRect.width / 2);
      var cy = clientY - (stageRect.top + stageRect.height / 2);
      var ratio = nextScale / zoom.scale;
      zoom.x = cx - (cx - zoom.x) * ratio;
      zoom.y = cy - (cy - zoom.y) * ratio;
      zoom.scale = nextScale;
      applyZoom();
    }

    function toggleZoom(clientX, clientY) {
      zoomAbout(zoom.scale > 1 ? 1 : ZOOM_TAP, clientX, clientY);
      if (zoom.scale === 1) {
        zoom.x = 0;
        zoom.y = 0;
        applyZoom();
      }
    }

    function pointerList() {
      return Object.keys(pointers).map(function (id) {
        return pointers[id];
      });
    }

    function onPointerDown(event) {
      if (event.button && event.button !== 0) return;
      pointers[event.pointerId] = { x: event.clientX, y: event.clientY };
      pointerCount = Object.keys(pointers).length;
      moved = false;
      if (modalStage.setPointerCapture) {
        try { modalStage.setPointerCapture(event.pointerId); } catch (e) { /* ignore */ }
      }
      if (pointerCount === 2) {
        var p = pointerList();
        pinchStart = {
          distance: Math.hypot(p[1].x - p[0].x, p[1].y - p[0].y),
          scale: zoom.scale,
        };
        dragStart = null;
      } else if (pointerCount === 1) {
        dragStart = { x: event.clientX, y: event.clientY, zx: zoom.x, zy: zoom.y };
      }
      event.preventDefault();
    }

    function onPointerMove(event) {
      if (!pointers[event.pointerId]) return;
      pointers[event.pointerId] = { x: event.clientX, y: event.clientY };
      if (pointerCount === 2 && pinchStart) {
        var p = pointerList();
        var distance = Math.hypot(p[1].x - p[0].x, p[1].y - p[0].y);
        if (!pinchStart.distance) return;
        var midX = (p[0].x + p[1].x) / 2;
        var midY = (p[0].y + p[1].y) / 2;
        // Pan by the midpoint's travel since the last move, then scale about where the
        // midpoint now is — so two fingers that drift while they spread carry the
        // picture with them.
        if (pinchStart.midX != null) {
          zoom.x += midX - pinchStart.midX;
          zoom.y += midY - pinchStart.midY;
        }
        pinchStart.midX = midX;
        pinchStart.midY = midY;
        zoomAbout(pinchStart.scale * Math.pow(distance / pinchStart.distance, PINCH_DAMPING), midX, midY);
        moved = true;
      } else if (pointerCount === 1 && dragStart && zoom.scale > 1) {
        var dx = event.clientX - dragStart.x;
        var dy = event.clientY - dragStart.y;
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
          moved = true;
          modalStage.setAttribute('data-dp-dragging', '');
        }
        zoom.x = dragStart.zx + dx;
        zoom.y = dragStart.zy + dy;
        applyZoom();
      }
    }

    function onPointerUp(event) {
      if (!pointers[event.pointerId]) return;
      delete pointers[event.pointerId];
      pointerCount = Object.keys(pointers).length;
      modalStage.removeAttribute('data-dp-dragging');
      if (pointerCount < 2) pinchStart = null;
      if (pointerCount === 1) {
        // The remaining finger continues as a drag from where it is now.
        var rest = pointerList()[0];
        dragStart = { x: rest.x, y: rest.y, zx: zoom.x, zy: zoom.y };
        return;
      }
      dragStart = null;
      if (event.type !== 'pointerup' || moved || event.pointerType === 'mouse') return;
      // A tap. Two within 300ms and 30px toggle the zoom; one alone, at fit, on the
      // letterbox rather than the photograph, closes — the same "click the backdrop"
      // rule the dialog had before it went full-screen.
      var now = Date.now();
      var near = Math.abs(event.clientX - lastTapX) < 30 && Math.abs(event.clientY - lastTapY) < 30;
      if (now - lastTap < 300 && near) {
        lastTap = 0;
        toggleZoom(event.clientX, event.clientY);
        return;
      }
      lastTap = now;
      lastTapX = event.clientX;
      lastTapY = event.clientY;
      if (zoom.scale === 1 && event.target !== modalImg) modal.close();
    }

    function onWheel(event) {
      event.preventDefault();
      var factor = event.deltaY < 0 ? WHEEL_STEP : 1 / WHEEL_STEP;
      zoomAbout(zoom.scale * factor, event.clientX, event.clientY);
    }

    function onKey(event) {
      var stageRect = modalStage.getBoundingClientRect();
      var cx = stageRect.left + stageRect.width / 2;
      var cy = stageRect.top + stageRect.height / 2;
      if (event.key === '+' || event.key === '=') {
        zoomAbout(zoom.scale * 1.25, cx, cy);
      } else if (event.key === '-' || event.key === '_') {
        zoomAbout(zoom.scale / 1.25, cx, cy);
      } else if (event.key === '0') {
        resetZoom();
      } else {
        return;
      }
      event.preventDefault();
    }

    function openModal() {
      if (!modalReady) return;
      var frame = frames[current];
      var img = frame && frame.querySelector('img');
      if (!img) return;
      if (img.getAttribute('srcset')) {
        modalImg.setAttribute('srcset', img.getAttribute('srcset'));
        modalImg.setAttribute('sizes', '100vw');
      } else {
        modalImg.removeAttribute('srcset');
        modalImg.removeAttribute('sizes');
      }
      modalImg.src = img.currentSrc || img.src;
      modalImg.alt = img.alt || '';
      resetZoom();
      modal.showModal();
    }

    if (modalReady) {
      modal.addEventListener('click', function (event) {
        if (!(event.target instanceof Element)) return;
        if (event.target.closest('[data-dp-gallery-modal-close]')) {
          modal.close();
          return;
        }
        // A mouse click on the letterbox, at fit, closes; touch taps are handled in
        // onPointerUp so a pinch or drag that ends off the picture never closes it.
        if (event.target === modalStage && zoom.scale === 1 && !moved && event.detail !== 0) {
          modal.close();
        }
      });

      modal.addEventListener('dblclick', function (event) {
        if (event.target !== modalImg && event.target !== modalStage) return;
        event.preventDefault();
        toggleZoom(event.clientX, event.clientY);
      });

      modalStage.addEventListener('pointerdown', onPointerDown);
      modalStage.addEventListener('pointermove', onPointerMove);
      modalStage.addEventListener('pointerup', onPointerUp);
      modalStage.addEventListener('pointercancel', onPointerUp);
      modalStage.addEventListener('wheel', onWheel, { passive: false });
      modal.addEventListener('keydown', onKey);
      // Safari's own page pinch and double-tap zoom bypass `touch-action` in some
      // versions; refusing the touch and gesture defaults here is the belt to that brace.
      modalStage.addEventListener('touchmove', function (event) { event.preventDefault(); }, { passive: false });
      modalStage.addEventListener('gesturestart', function (event) { event.preventDefault(); });

      // Fires for every way out — close disc, Escape, backdrop — so the teardown cannot
      // be skipped by the path this file did not think of.
      modal.addEventListener('close', function () {
        modalImg.removeAttribute('src');
        modalImg.removeAttribute('srcset');
        modalImg.removeAttribute('sizes');
        resetZoom();
      });
    }

    gallery.addEventListener('click', function (event) {
      if (!(event.target instanceof Element)) return;
      var thumb = event.target.closest('[data-dp-gallery-thumb]');
      if (thumb) {
        show(thumb.getAttribute('data-dp-gallery-thumb'));
        return;
      }
      if (event.target.closest('[data-dp-gallery-zoom]')) {
        openModal();
        return;
      }
      /* A click anywhere on the stage photograph opens it too — the disc is the visible,
         focusable affordance, not the only target. Touch scrolling never synthesises a
         click, so the carousel drag on narrow screens is unaffected. The clicked frame
         becomes the current one first: in the grid every frame is clickable, and the
         lightbox must open on the photograph that was clicked, not on whichever index
         the last thumb or swipe left behind. */
      var frame = event.target.closest('[data-dp-gallery-frame]');
      if (frame) {
        current = parseInt(frame.getAttribute('data-dp-gallery-frame'), 10) || 0;
        openModal();
      }
    });

    /* The step-on-lift swipe, kept for the WIDE layout only — a touch laptop, or a
       tablet above the breakpoint, where the stage is not a scroll container and there
       is nothing else to move it. Below the breakpoint the browser's own scrolling has
       the gesture, and running this as well would step a second frame on every flick. */
    var startX = 0;
    var startY = 0;
    var tracking = false;

    stage.addEventListener(
      'touchstart',
      function (event) {
        if (carousel || grid) return;
        if (event.touches.length !== 1) return;
        tracking = true;
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
      },
      { passive: true }
    );

    stage.addEventListener(
      'touchend',
      function (event) {
        if (!tracking) return;
        tracking = false;
        if (carousel || grid) return;
        var touch = event.changedTouches[0];
        if (!touch) return;
        var deltaX = touch.clientX - startX;
        var deltaY = touch.clientY - startY;
        if (Math.abs(deltaX) < 48) return;
        if (Math.abs(deltaX) < Math.abs(deltaY) * 1.5) return;
        var step = deltaX < 0 ? 1 : -1;
        var next = (current + step + frames.length) % frames.length;
        show(String(next));
      },
      { passive: true }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
